import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import type { Reflection, FilterOptions } from "../types";
import { filterReflections, sortReflections } from "../utils";

export const useReflections = (
	userId: string | undefined,
	filterOptions: FilterOptions,
) => {
	const [reflections, setReflections] = useState<Reflection[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchReflections = async () => {
			try {
				// 🔒 SECURITY: Verify user is authenticated before fetching data
				if (!userId) {
					console.error("No authenticated user - cannot fetch reflections");
					setReflections([]);
					setLoading(false);
					return;
				}

				// 🔒 SECURITY: Only fetch reflections belonging to the authenticated user
				const { data, error } = await supabase
					.from("reflections")
					.select("*")
					.eq("user_id", userId) // CRITICAL: Add user filtering to prevent data exposure
					.order("updated_at", { ascending: false });

				if (error) {
					console.error("Error fetching reflections:", error);
					return;
				}

				const mapped = (data || []).map((r: any) => {
					// If is_draft is explicitly false/true, respect it. Otherwise infer draft status.
					const hasExplicit = typeof r.is_draft === "boolean";
					const inferredDraft =
						!r.description || r.description.trim().length === 0;
					return {
						...r,
						is_draft: hasExplicit ? r.is_draft : inferredDraft,
					} as Reflection;
				});
				setReflections(mapped);
			} catch (error) {
				console.error("Error fetching reflections:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchReflections();
	}, [userId]);

	const filteredReflections = useMemo(() => {
		const filtered = filterReflections(
			reflections,
			filterOptions.searchTerm,
			filterOptions.statusFilter,
		);
		return sortReflections(filtered, filterOptions.sortBy);
	}, [reflections, filterOptions]);

	const refetch = async () => {
		if (!userId) return;

		setLoading(true);
		const { data, error } = await supabase
			.from("reflections")
			.select("*")
			.eq("user_id", userId)
			.order("updated_at", { ascending: false });

		if (!error && data) {
			const mapped = (data || []).map((r: any) => {
				const hasExplicit = typeof r.is_draft === "boolean";
				const inferredDraft =
					!r.description || r.description.trim().length === 0;
				return {
					...r,
					is_draft: hasExplicit ? r.is_draft : inferredDraft,
				} as Reflection;
			});
			setReflections(mapped);
		}
		setLoading(false);
	};

	return {
		reflections,
		filteredReflections,
		loading,
		refetch,
		setReflections,
	};
};
